import {Controller} from "@hotwired/stimulus"
import {debounce} from "../libs/utils"

/**
 * Controller used for Form::DropdownComponent.
 */
export default class DropdownController extends Controller {
  static values = {
    searchEnabled: Boolean,
    placeholder: String,
    extraInput: Boolean,
  };

  static targets = [
    'button', 'label',
    'resultInput',
    'extraInput', // incorporate another input for extra params for the Form
    'searchInput',
    'blankItem'
  ];

  initialize() {
    this._doSearch = debounce(this._doSearch.bind(this), 400);

    this._selectedButtons = [];
    this._currentSelectedIndex = 0;
  }

  connect() {
    this.dropdownBS = new bootstrap.Dropdown(this.element);
    console.debug("Dropdown controller - ", 'Enable search:', this.searchEnabledValue, this.dropdownBS);

    this._setInitialValue();

    this.element.addEventListener('show.bs.dropdown', (event) => {
      console.debug('Dropdown opened');

      setTimeout(() => {
        this.searchInputTarget.focus();
      });
    }, false);

    if (this.searchEnabledValue) {
      this._doSearch();

      // Prevent input to propagate the input event to the form
      this.searchInputTarget.addEventListener('input', (event) => event.stopPropagation(), false);
      this.searchInputTarget.addEventListener('change', (event) => event.stopPropagation(), false);

      this.searchInputTarget.addEventListener('keydown', (event) => {
        console.debug("=> keydown", event);

        if (event.key === 'Enter') {
          event.preventDefault();
          this._selectItem();
          return;
        }

        if (event.key === 'ArrowUp') {
          this._selectItemUp();
          return;
        }

        if (event.key === 'ArrowDown') {
          this._selectItemDown();
          return;
        }

        this._doSearch();
      }, false);
    }
  }

  _selectItem() {
    const button = this._selectedButtons[this._currentSelectedIndex];

    if (button) {
      // Showing all items
      for (const buttonTarget of this.buttonTargets) {
        buttonTarget.classList.remove('active', 'd-none');
      }

      this._selectedButtons = this.buttonTargets;
      this._currentSelectedIndex = 0;
      this._selectCurrentItem();

      // setting value to the input form
      this.dropdownBS.hide();
      this.searchInputTarget.value = '';

      setTimeout(() => this.labelTarget.focus());

      if (this.extraInputValue && button.dataset.recordExtra) {
        console.log("=> Extra Value ");
        this.extraInputTarget.value = button.dataset.recordExtra;
      }

      this.value = button.dataset.recordId;
    }
  }

  _selectCurrentItem() {
    this._selectedButtons[this._currentSelectedIndex].classList.add('active')
  }

  _selectItemDown() {
    this._selectedButtons[this._currentSelectedIndex].classList.remove('active')

    if (this._currentSelectedIndex === this._selectedButtons.length - 1) {
      console.debug('You are in the last item');
      this._currentSelectedIndex = 0;
    } else {
      this._currentSelectedIndex += 1;
    }

    const currentItem = this._selectedButtons[this._currentSelectedIndex]
    currentItem.classList.add('active')
    currentItem.scrollIntoView({behavior: 'auto', block: 'nearest'});
  }

  _selectItemUp() {
    this._selectedButtons[this._currentSelectedIndex].classList.remove('active');

    if (this._currentSelectedIndex === 0) {
      console.debug('You are in the first item');
      this._currentSelectedIndex = this._selectedButtons.length - 1;
    } else {
      this._currentSelectedIndex -= 1;
    }

    const currentItem = this._selectedButtons[this._currentSelectedIndex]
    currentItem.classList.add('active')
    currentItem.scrollIntoView({behavior: 'auto', block: 'nearest'});
  }

  _doSearch() {
    console.debug('=> Filter by', this.searchInputTarget.value);

    this._selectedButtons = [];
    this._currentSelectedIndex = 0;

    const searchInput = this.searchInputTarget.value ?
      this.searchInputTarget.value.trim() : '';

    if (searchInput === '') {
      this._selectedButtons = this.buttonTargets;
      const currentButton = this._selectedButtons[this._currentSelectedIndex];

      for (const buttonTarget of this.buttonTargets) {
        buttonTarget.classList.remove('d-none', 'active');

        if (buttonTarget === currentButton) {
          buttonTarget.classList.add('active')
        }
      }

      return;
    }

    // Reg exp match
    // const regExp = new RegExp(searchInput, 'ig');
    //
    // for (const buttonTarget of this.buttonTargets) {
    //   if (regExp.test(buttonTarget.innerHTML.trim())) {
    //     buttonTarget.classList.remove('d-none', 'active');
    //     this._selectedButtons.push(buttonTarget);
    //   } else {
    //     buttonTarget.classList.add('d-none');
    //   }
    // }

    const inputQuery = searchInput.toLowerCase();

    for (const buttonTarget of this.buttonTargets) {
      if (buttonTarget.innerHTML.trim().toLowerCase().includes(inputQuery)) {
        buttonTarget.classList.remove('d-none', 'active');
        this._selectedButtons.push(buttonTarget);
      } else {
        buttonTarget.classList.add('d-none');
      }
    }

    if (this._selectedButtons.length > 0) {
      this._selectCurrentItem();
    }
  }

  /**
   * Set the initial value without triggering a new input event
   *
   * @private
   */
  _setInitialValue() {
    if (this.placeholderValue) {
      this.labelTarget.innerHTML = this.placeholderValue;
      return;
    }

    const button = this._findButtonById(this.fieldForm.value);
    if (button) {
      this.labelTarget.innerHTML = button.dataset.recordLabel;
    }

    console.debug("DropdownController: Current dropdown value ", this.fieldForm);
  }

  _findButtonById(recordId) {
    for (const buttonTarget of this.buttonTargets) {
      if (buttonTarget.dataset.recordId === recordId) {
        return buttonTarget;
      }
    }
  }

  recordSelected(event) {
    console.debug("=> Item was selected manually", event.params);

    if (this.extraInputValue && event.params.recordExtra) {
      this.extraInputTarget.value = event.params.recordExtra;
    }

    this.value = event.params.recordId;
  }

  get value() {
    return this.resultInputTarget.value;
  }

  set value(recordId) {
    this.fieldForm.value = recordId;

    const button = this._findButtonById(recordId.toString());
    console.log("=> Current dropdown value ", this.fieldForm);
    this.labelTarget.innerHTML = button.dataset.recordLabel;

    this.element.dispatchEvent(new CustomEvent('dropdown-change', {detail: recordId}));

    // bubbles attribute makes it the form catches the event
    // The important input is this
    this.fieldForm.dispatchEvent(new CustomEvent('input', {detail: recordId, bubbles: true}));
  }

  get fieldForm() {
    return this.resultInputTarget;
  }
}
